/**
 * Cities: founding, the tiles their citizens work, and growth.
 *
 * The economic spine of the whole game starts here — terrain becomes tile yields,
 * tile yields become food, food becomes population, and population works more tiles.
 * Everything in later phases (production, science, culture, unhappiness) hangs off
 * this loop, so it is kept small and readable rather than clever.
 *
 * Every number lives in `content/rules.json` under `cities`. None of the balance is
 * in this file, which is the point: growth pacing is the single most-tuned figure in
 * a game like this and Mark must be able to change it without a build.
 */

import { traitSum } from '../civs/traits.js';
import { resourceKnown } from '../resources/known.js';
import type { Content } from '../../../content/schema/index.js';
import { hexSpiral } from '../hex/index.js';
import { hexIndex, indexToHex, tileIndex } from '../state/grid.js';
import { NONE, type City, type Civ, type GameState, type Id, type TileGrid } from '../state/types.js';
import { addBuilding, effectTotal, isAtSizeCap } from './production.js';
import { chooseWorkedTiles, scoreTile, type TileOption, type WorkWeights } from './governor.js';
import { inDisorder } from './mood.js';
import { civEffectTotal } from './wonders.js';
import { recomputeBorders } from '../culture/index.js';
import { governedYield, governmentEffects } from '../government/index.js';

export interface Yield {
  food: number;
  production: number;
  gold: number;
}

/**
 * What a tile produces as it stands: terrain, plus feature, plus resource, plus a
 * river if one runs through it.
 *
 * The river bonus is the only reason an inland empire has any commerce at all before
 * roads exist. Without it the gold yields in terrain.json belong entirely to water
 * tiles, which would mean a landlocked civilisation could never keep a building
 * standing — found by the first test run after upkeep was introduced.
 */
const NO_YIELD = { food: 0, production: 0, gold: 0 };

/**
 * What a tile yields. Given a civilisation, a strategic resource it has not yet revealed
 * adds nothing — it does not know the iron is there (ADR-0063).
 */
export function tileYield(map: TileGrid, content: Content, index: number, civ?: Civ): Yield {
  const terrain = content.terrain.get(map.terrain[index]!);
  const feature = content.features.get(map.feature[index]!);
  const id = map.resource[index]!;
  const resource = content.resources.get(civ === undefined || id === 0 || resourceKnown(content, civ, id) ? id : 0);
  const improvement = content.improvements.get(map.improvement[index]!);
  const river = map.rivers[index] !== 0 && !terrain.water ? content.rules.cities.riverGoldBonus : 0;
  // A road adds its own yield on top of whatever else is built there.
  const road = map.road[index] === 1 && content.route ? content.route.yields : NO_YIELD;
  return {
    food: terrain.yields.food + feature.yields.food + resource.yields.food + improvement.yields.food + road.food,
    production:
      terrain.yields.production + feature.yields.production + resource.yields.production + improvement.yields.production +
      road.production,
    gold: terrain.yields.gold + feature.yields.gold + resource.yields.gold + improvement.yields.gold + river + road.gold,
  };
}

/**
 * The city centre's own yield, which is never allowed to be nothing.
 *
 * A city founded on desert would otherwise produce zero food and starve on the turn
 * it was founded, which reads as a bug however defensible the arithmetic is.
 */
export function centreYield(map: TileGrid, content: Content, index: number): Yield {
  const base = tileYield(map, content, index);
  return {
    food: Math.max(base.food, content.rules.cities.centreMinFood),
    production: Math.max(base.production, content.rules.cities.centreMinProduction),
    gold: Math.max(base.gold, content.rules.cities.centreMinGold),
  };
}

export function cityIndex(map: TileGrid, city: City): number {
  return tileIndex(map, city.col, city.row);
}

/** Food needed in store for the next citizen. Grows with size, so cities slow down. */
export function growthThreshold(content: Content, population: number): number {
  return content.rules.cities.growthBase + content.rules.cities.growthPerPop * population;
}

/** Tiles a city could work: within its radius, not the centre, not off the map. */
export function workableTiles(map: TileGrid, content: Content, city: City): number[] {
  const centre = cityIndex(map, city);
  const out: number[] = [];
  for (const h of hexSpiral(indexToHex(map, centre), content.rules.cities.workRadius)) {
    const index = hexIndex(map, h);
    if (index === -1 || index === centre) continue;
    out.push(index);
  }
  return out.sort((a, b) => a - b);
}

/**
 * Whether food this city grows beyond what its citizens eat is thrown away: it is at its
 * size limit and its store is already full, so there is nowhere for the food to go.
 */
export function foodIsWasted(content: Content, city: City): boolean {
  return isAtSizeCap(content, city) && city.foodStore >= growthThreshold(content, city.population);
}

/**
 * What the governor values a tile's yields at, for this city. Normally the rules'
 * `workWeights`; for a city whose spare food would be thrown away, food counts for
 * `capFoodWeight` instead, so the citizens work shields and trade — the governor's
 * promise to keep the city fed still comes first (ADR-0044, ADR-0069).
 */
export function governorWeights(content: Content, city: City): WorkWeights {
  const rules = content.rules.cities;
  return foodIsWasted(content, city) ? { ...rules.workWeights, food: rules.capFoodWeight } : rules.workWeights;
}

/**
 * Assign this city's citizens to the best tiles available, and record the claim.
 *
 * A deliberately simple governor: score every workable tile, take the best ones, ties
 * broken by tile index so the result never depends on iteration order, then make sure
 * the city is fed (governor.ts). The one thing it knows about the city's needs is
 * whether spare food would be thrown away (`governorWeights`).
 *
 * Tiles owned by another civilisation are refused; tiles owned by nobody are allowed,
 * because border growth is Phase 9 and a city limited to its first ring would stall
 * at six citizens with nothing to do about it.
 */
export function assignWorkedTiles(state: GameState, content: Content, city: City): void {
  const map = state.map;
  const weights = governorWeights(content, city);

  // A city the player has arranged by hand keeps its arrangement. The governor still
  // drops tiles that stopped being workable and fills any gap that leaves, because a
  // citizen standing on ground the city has lost is not a choice anybody made.
  if (!city.governed) {
    pruneWorkedTiles(state, content, city);
    return;
  }

  // Release this city's existing claims before choosing again, or its own tiles would
  // look taken to it.
  for (const index of city.workedTiles) {
    if (map.workedBy[index] === city.id) map.workedBy[index] = NONE;
  }

  const options = workableTiles(map, content, city)
    .filter((index) => {
      const owner = map.owner[index]!;
      if (owner !== NONE && owner !== city.owner) return false;
      const claimed = map.workedBy[index]!;
      return claimed === NONE || claimed === city.id;
    })
    .map((index) => ({ index, ...workedYield(state, content, city.owner, index) }));

  // What the worked tiles must grow between them for the city not to shrink: everyone
  // eats, and the centre tile feeds some of them for free. See governor.ts.
  const foodNeeded =
    city.population * content.rules.cities.foodPerCitizen - cityCentreYield(state, content, city).food;
  const chosen = chooseWorkedTiles(options, city.population, foodNeeded, weights);
  city.workedTiles = chosen;
  const byIndex = new Map(options.map((o) => [o.index, o]));
  let food = chosen.reduce((sum, i) => sum + (byIndex.get(i)?.food ?? 0), 0);

  // A city whose spare food is thrown away is better served by a scholar than by a
  // citizen on a tile that grows little but food. Take citizens off the tiles worth less
  // than the specialist they would become — never so many that the city goes hungry
  // (ADR-0069). Science and gold are valued alike, at the weight of gold.
  if (foodIsWasted(content, city)) {
    const kind =
      city.specialist === NONE
        ? content.specialists.byKey.get(content.rules.cities.autoSpecialist)
        : content.specialists.all[city.specialist];
    const specialist = kind ? (kind.yields.science + kind.yields.gold) * weights.gold : 0;
    for (;;) {
      let drop: TileOption | null = null;
      for (const index of city.workedTiles) {
        const tile = byIndex.get(index);
        if (!tile || scoreTile(tile, weights) >= specialist || food - tile.food < foodNeeded) continue;
        if (!drop || scoreTile(tile, weights) < scoreTile(drop, weights) || (scoreTile(tile, weights) === scoreTile(drop, weights) && tile.index < drop.index)) {
          drop = tile;
        }
      }
      if (!drop) break;
      food -= drop.food;
      const dropped = drop.index;
      city.workedTiles = city.workedTiles.filter((i) => i !== dropped);
    }
  }

  // A city on Auto would rather have entertainers than riot. While it is in disorder,
  // take citizens off their least valuable tiles — never so many that it starts to
  // starve, because a hungry city shrinks and a rioting one merely stops building.
  // Disorder costs every shield and every point of science, so one citizen watching a
  // play instead of working a hill is nearly always the better trade.
  if (city.specialist === NONE) {
    while (city.workedTiles.length > 0 && inDisorder(state, content, city)) {
      let drop: TileOption | null = null;
      for (const index of city.workedTiles) {
        const tile = byIndex.get(index);
        if (!tile || food - tile.food < foodNeeded) continue;
        if (!drop || scoreTile(tile, weights) < scoreTile(drop, weights) || (scoreTile(tile, weights) === scoreTile(drop, weights) && tile.index < drop.index)) {
          drop = tile;
        }
      }
      if (!drop) break;
      food -= drop.food;
      const dropped = drop.index;
      city.workedTiles = city.workedTiles.filter((i) => i !== dropped);
    }
  }

  for (const index of city.workedTiles) map.workedBy[index] = city.id;
}

/**
 * Everything the city produces this turn: centre tile, worked tiles, and whatever its
 * buildings add on top.
 */
/** Whether a tile can be worked by this city at all: in range, free, and not foreign. */
export function canWorkTile(state: GameState, content: Content, city: City, index: number): boolean {
  const map = state.map;
  if (index === cityIndex(map, city)) return false;
  if (!workableTiles(map, content, city).includes(index)) return false;
  const owner = map.owner[index]!;
  if (owner !== NONE && owner !== city.owner) return false;
  const claimed = map.workedBy[index]!;
  return claimed === NONE || claimed === city.id;
}

/**
 * Keep a hand-picked arrangement legal: drop what can no longer be worked, and fill
 * back up to the population from the best of what is left.
 */
function pruneWorkedTiles(state: GameState, content: Content, city: City): void {
  const map = state.map;
  const weights = governorWeights(content, city);

  const kept = city.workedTiles.filter((index) => {
    const owner = map.owner[index]!;
    if (owner !== NONE && owner !== city.owner) return false;
    const claimed = map.workedBy[index]!;
    return claimed === NONE || claimed === city.id;
  });

  for (const index of city.workedTiles) {
    if (!kept.includes(index) && map.workedBy[index] === city.id) map.workedBy[index] = NONE;
  }

  if (kept.length > city.population) kept.length = city.population;

  if (kept.length < city.population) {
    const spare = workableTiles(map, content, city)
      .filter((index) => !kept.includes(index) && canWorkTile(state, content, city, index))
      .map((index) => {
        const y = workedYield(state, content, city.owner, index);
        return { index, score: y.food * weights.food + y.production * weights.production + y.gold * weights.gold };
      })
      .sort((a, b) => (b.score - a.score) || (a.index - b.index));
    for (const tile of spare) {
      if (kept.length >= city.population) break;
      kept.push(tile.index);
    }
  }

  kept.sort((a, b) => a - b);
  city.workedTiles = kept;
  for (const index of kept) map.workedBy[index] = city.id;
}

/**
 * A tile's yield as worked by a city of this civilisation: its government may waste
 * rich land or add trade to it (ADR-0059).
 */
export function workedYield(state: GameState, content: Content, civId: Id, index: number): Yield {
  return governedYield(governmentEffects(state, content, civId), tileYield(state.map, content, index, state.civs[civId]));
}

/** The city centre's yield under its owner's government, never below the centre minimums. */
export function cityCentreYield(state: GameState, content: Content, city: City): Yield {
  const index = cityIndex(state.map, city);
  const rules = content.rules.cities;
  const y = governedYield(governmentEffects(state, content, city.owner), tileYield(state.map, content, index, state.civs[city.owner]));
  // An agricultural people's city tiles feed one more (ADR-0062).
  y.food += traitSum(content, state.civs[city.owner], 'centreFood');
  return {
    food: Math.max(y.food, rules.centreMinFood),
    production: Math.max(y.production, rules.centreMinProduction),
    gold: Math.max(y.gold, rules.centreMinGold),
  };
}

export function cityYield(state: GameState, content: Content, city: City): Yield {
  const total = cityCentreYield(state, content, city);
  for (const index of city.workedTiles) {
    const y = workedYield(state, content, city.owner, index);
    total.food += y.food;
    total.production += y.production;
    total.gold += y.gold;
  }
  total.production += effectTotal(content, city, 'productionBonus') + civEffectTotal(state, content, city.owner, 'productionBonus');
  const percent = governmentEffects(state, content, city.owner).productionPercent;
  if (percent !== 0) total.production = Math.max(0, Math.floor((total.production * (100 + percent)) / 100));
  total.gold +=
    effectTotal(content, city, 'goldBonus') +
    civEffectTotal(state, content, city.owner, 'goldBonus') +
    traitSum(content, state.civs[city.owner], 'goldPerCity');
  return total;
}

/** Food left after the citizens have eaten. Negative means the city is starving. */
export function foodSurplus(state: GameState, content: Content, city: City): number {
  return cityYield(state, content, city).food - city.population * content.rules.cities.foodPerCitizen;
}

/**
 * Settle who owns what after cities appear, change hands or grow their borders, and
 * let any city that lost a worked tile choose again (ADR-0057).
 */
export function settleBorders(state: GameState, content: Content): void {
  for (const id of recomputeBorders(state, content)) {
    const city = state.cities[id];
    if (city) assignWorkedTiles(state, content, city);
  }
}

/**
 * The next unused city name for a civilisation.
 *
 * Real toponyms from the culture, in order, per ADR-0008. Once the list is exhausted
 * names repeat with a numeral, which is what actually happens to colonial place names
 * and is better than running out.
 */
export function nextCityName(state: GameState, content: Content, civId: Id): string {
  const civ = state.civs[civId];
  const names = civ ? content.civilizations.get(civ.civType).cityNames : [];
  const used = new Set(
    Object.values(state.cities)
      .filter((c) => c.owner === civId)
      .map((c) => c.name),
  );

  for (const name of names) if (!used.has(name)) return name;

  for (let suffix = 2; ; suffix++) {
    for (const name of names) {
      const candidate = `${name} ${suffix}`;
      if (!used.has(candidate)) return candidate;
    }
    if (names.length === 0) return `City ${used.size + 1}`;
  }
}

/** Create a city at a tile. Assumes the placement has already been validated. */
export function createCity(state: GameState, content: Content, owner: Id, index: number): City {
  const map = state.map;
  const col = index % map.width;
  const city: City = {
    id: state.nextId++,
    owner,
    col,
    row: (index - col) / map.width,
    name: nextCityName(state, content, owner),
    population: content.rules.cities.startingPopulation,
    foodStore: 0,
    productionStore: 0,
    culture: 0,
    borderRadius: content.rules.cities.startingBorderRadius,
    unrest: 0,
    foundedTurn: state.meta.turn,
    workedTiles: [],
    governed: true,
    specialist: NONE,
    buildings: [],
    queue: [],
  };

  state.cities[city.id] = city;
  // Every city stands on a road: it is where the roads lead (ADR-0055).
  map.road[index] = 1;

  // The first city a civilisation founds becomes its seat of government. Somewhere has
  // to be the centre from the start, or administrative drag has no origin to measure
  // from and every city would be equally far from nowhere.
  const seat = content.buildings.all.find((b) => b.effects.isSeatOfGovernment === true);
  const hasCapital = Object.values(state.cities).some(
    (c) => c.owner === owner && c.id !== city.id && c.buildings.some((id) => content.buildings.get(id).effects.isSeatOfGovernment),
  );
  if (seat && !hasCapital) addBuilding(city, seat.id);

  // A new city's own tile and first ring are always its own; beyond that, ground goes
  // to whoever has the most culture on it — which is no one yet, for a new city.
  settleBorders(state, content);
  assignWorkedTiles(state, content, city);
  return city;
}

export function citiesOf(state: GameState, civId: Id): City[] {
  return Object.values(state.cities)
    .filter((c) => c.owner === civId)
    .sort((a, b) => a.id - b.id);
}

/** A city already standing on this tile, if any. */
export function cityAt(state: GameState, map: TileGrid, index: number): City | null {
  for (const city of Object.values(state.cities)) {
    if (cityIndex(map, city) === index) return city;
  }
  return null;
}
